import { useQuery } from '@apollo/client'
import { ILesson } from '@dtos/Lesson'
import { GET_ADMIN_PREVIEW_PATH } from '@services/api/graphql/queries/getAdminPreviewPath'

export interface IAdminPreviewPathCourse {
  id: string
  imageUrl?: string | null
  name: string
  kind?: string | null
  lessons?: { nodes: ILesson[] } | null
}

export interface IAdminPreviewPath {
  id: string
  name: string
  coursesInOrder: IAdminPreviewPathCourse[]
}

/**
 * Trilha do preview na forma do getNewPathEnrollment — o PathContent e o
 * PathProductsSection só sabem ler `enrollment.progressCard.courseProgressCards`.
 *
 * Os ids de matrícula recebem o id da trilha pelo mesmo motivo do
 * toPreviewEnrollment: os cards fazem `.toString()` neles, e em preview o
 * productRedirectToEnrollment ignora o enrollmentId. `unlimited` esconde o
 * selo de prazo de acesso, que não existe sem matrícula; progresso fica
 * ausente, então os cards aparecem como não iniciados.
 */
export const toPreviewPathEnrollment = (path: IAdminPreviewPath) => ({
  enrollment: {
    id: path.id,
    unlimited: true,
    activatedAt: null,
    availableUntil: null,
    schoolProduct: {
      id: path.id,
      title: path.name,
      expireDate: null,
      availableTimeLength: null,
    },
    progressCard: {
      id: path.id,
      courseProgressCards: path.coursesInOrder.map((course) => {
        const firstLesson = course.lessons?.nodes?.[0]

        return {
          id: course.id,
          courseId: course.id,
          course: {
            ...course,
            // Mesmo caminho que o PathProductsSection lê para a primeira aula.
            courseModules: {
              nodes: firstLesson
                ? [
                    {
                      courseContents: {
                        nodes: [
                          {
                            id: firstLesson.courseContentId,
                            lesson: firstLesson,
                          },
                        ],
                      },
                    },
                  ]
                : [],
            },
          },
        }
      }),
    },
  },
})

export const ADMIN_PREVIEW_PATH_PAGE_SIZE = 10

export const useAdminPreviewPath = (
  id: number,
  options?: { skip?: boolean },
) => {
  const { data, loading, error, fetchMore } = useQuery<{
    adminPreviewPath: IAdminPreviewPath | null
  }>(GET_ADMIN_PREVIEW_PATH, {
    variables: { id, offset: 0, limit: ADMIN_PREVIEW_PATH_PAGE_SIZE },
    skip: options?.skip || !id || isNaN(id),
    errorPolicy: 'all',
  })

  return { data, loading, error, fetchMore }
}
